import * as Api from '@ViewModels';
// import { integrationSources } from '../../extViewmodels/Utils';

/**
 * Returns true if the given source string corresponds to a v1 integration
 * (a member of IAccountIntegrations) that is currently enabled on this account.
 * The list of cases is intentionally a verbatim port of the chain that
 * previously lived inline in canEditKeyFact and contactIsFromEnabledIntegration.
 */
const isSourceEnabledV1Integration = (
	source: string | null | undefined,
	integrations?: Api.IAccountIntegrations
): boolean => {
	if (!source) return false;
	if (source === Api.IntegrationSources.InternalLevitate) return true;
	if (!integrations) return false;
	return (
		(source === Api.IntegrationSources.HawkSoft && !!integrations.hawkSoft?.enabled) ||
		(source === Api.IntegrationSources.AMS360 && !!integrations.ams360?.enabled) ||
		(source === Api.IntegrationSources.Redtail && !!integrations.redtail?.enabled) ||
		(source === Api.IntegrationSources.Eclipse && !!integrations.eclipse?.enabled) ||
		(source === Api.IntegrationSources.Clio && !!integrations.clio?.enabled) ||
		(source === Api.IntegrationSources.EpicCsv && !!integrations.epicCsv?.enabled) ||
		(source === Api.IntegrationSources.EzLynxCsv && !!integrations.ezLynxCsv?.enabled) ||
		(source === Api.IntegrationSources.QQCatalyst && !!integrations.qqCatalyst?.enabled) ||
		(source === Api.IntegrationSources.GeneralCsv && !!integrations.generalCsv?.enabled) ||
		(source === Api.IntegrationSources.DonorPerfect && !!integrations.donorPerfect?.enabled)
	);
};

/**
 * Returns true if the given source string corresponds to a v2 integration
 * (IExternalIntegration) that is currently enabled on this account.
 */
const isSourceEnabledV2Integration = (
	source: string | null | undefined,
	externalIntegrations?: Partial<Api.IExternalIntegration>[]
): boolean => {
	if (!source) return false;
	if (!externalIntegrations) return false;
	return externalIntegrations.some(i => i.provider === source && i.enabled);
};

/**
 * Returns true if the given source string corresponds to a managed integration
 * (v1 IAccountIntegrations or v2 IExternalIntegration) that is currently
 * enabled on this account. Non-integration sources -— including CsvImport,
 * Zapier and other public-API consumers, and any future or unknown source —-
 * return false.
 */
export const isSourceEnabledIntegration = (
	source: string | null | undefined,
	integrations: Api.IAccountIntegrations,
	externalIntegrations?: Partial<Api.IExternalIntegration>[]
): boolean => {
	if (!source) return false;
	return (
		isSourceEnabledV2Integration(source, externalIntegrations) || isSourceEnabledV1Integration(source, integrations)
	);
};

/**
 * Checks if contact is from enabled integration
 * @source Original location: UiUtils.ts.backup line 1716
 */
export const contactIsFromEnabledIntegration = (
	contact: Api.ContactViewModel,
	integrations: Api.IAccountIntegrations
): boolean => isSourceEnabledV1Integration(contact.source, integrations);

/**
 * Gets contact source
 * @source Original location: UiUtils.ts.backup line 1742
 */
export const getContactSource = (contact: Api.ContactViewModel, integrations: Api.IAccountIntegrations) => {
	const source = contact.source;

	if (source === Api.IntegrationSources.EpicCsv) {
		return 'Epic';
	}

	if (source === Api.IntegrationSources.EzLynxCsv) {
		return 'EzLynx';
	}

	if (source === Api.IntegrationSources.GeneralCsv) {
		return integrations.generalCsv.generalCsvProvider;
	}

	return source;
};

const titleCase = (value: string) => {
	if (!value) return '';
	const newValue = value.toLowerCase();
	return `${newValue[0].toUpperCase()}${newValue.slice(1)}`;
};

/**
 * Gets integration name by key
 * @source Original location: UiUtils.ts.backup line 1774
 */
export const getIntegrationNameByKey = (key: keyof Api.IAccountIntegrations): string => {
	// I do not like magic strings, but I could not get anything else working
	if (key === 'hawkSoft') return 'HawkSoft';
	if (key === 'ams360') return 'AMS360';
	if (key === 'redtail') return 'Redtail';
	if (key === 'eclipse') return 'Eclipse';
	if (key === 'clio') return 'Clio';
	if (key === 'epicCsv') return 'Epic';
	if (key === 'ezLynxCsv') return 'EzLynx';
	if (key === 'qqCatalyst') return 'QQ Catalyst';
	if (key === 'donorPerfect') return 'DonorPerfect';

	return titleCase(key);
};

/**
 * Gets display name for V2 integration provider enum
 */
export const getIntegrationProviderDisplayName = (provider: Api.IntegrationProvider): string => {
	const displayNames: Partial<Record<Api.IntegrationProvider, string>> = {
		[Api.IntegrationProvider.HawkSoft]: 'HawkSoft',
		[Api.IntegrationProvider.AMS360]: 'AMS360',
		[Api.IntegrationProvider.Redtail]: 'Redtail',
		[Api.IntegrationProvider.Eclipse]: 'Eclipse',
		[Api.IntegrationProvider.Clio]: 'Clio',
		[Api.IntegrationProvider.EzLynxCsv]: 'EzLynx',
		[Api.IntegrationProvider.EpicCsv]: 'Epic',
		[Api.IntegrationProvider.QQCatalyst]: 'QQ Catalyst',
		[Api.IntegrationProvider.HubSpot]: 'HubSpot',
		[Api.IntegrationProvider.Wealthbox]: 'Wealthbox',
		[Api.IntegrationProvider.MyCase]: 'MyCase',
		[Api.IntegrationProvider.DonorPerfect]: 'DonorPerfect',
		[Api.IntegrationProvider.PracticePanther]: 'PracticePanther',
		[Api.IntegrationProvider.MicrosoftDynamics365Sales]: 'Microsoft Dynamics 365',
		[Api.IntegrationProvider.Salesforce]: 'Salesforce',
		[Api.IntegrationProvider.ServiceTitan]: 'ServiceTitan',
		[Api.IntegrationProvider.QuickBooksOnline]: 'QuickBooks Online',
		[Api.IntegrationProvider.HousecallPro]: 'Housecall Pro',
		[Api.IntegrationProvider.Bloomerang]: 'Bloomerang',
	};
	return displayNames[provider] || provider;
};

/**
 * Gets display names for all enabled integrations (both V1 and V2)
 * @param v1Integrations - V1 integrations from account.integrations
 * @param v2Integrations - V2 integrations from useGetExternalIntegrationsQuery
 * @returns Array of unique display names for enabled integrations
 */
export const getEnabledIntegrationDisplayNames = (
	v1Integrations: Api.IAccountIntegrations | undefined,
	v2Integrations: Api.IExternalIntegration[] | undefined
): string[] => {
	const enabledNames = new Set<string>();

	// Check V1 integrations
	if (v1Integrations) {
		const v1Keys: (keyof Api.IAccountIntegrations)[] = [
			'hawkSoft',
			'ams360',
			'redtail',
			'eclipse',
			'clio',
			'epicCsv',
			'ezLynxCsv',
			'qqCatalyst',
			'donorPerfect',
			'hubSpot',
		];

		for (const key of v1Keys) {
			const integration = v1Integrations[key] as Api.IIntegration | undefined;
			if (integration?.enabled) {
				enabledNames.add(getIntegrationNameByKey(key));
			}
		}
	}

	// Check V2 integrations
	if (v2Integrations) {
		for (const integration of v2Integrations) {
			if (integration.enabled && integration.provider !== Api.IntegrationProvider.None) {
				enabledNames.add(getIntegrationProviderDisplayName(integration.provider));
			}
		}
	}

	return Array.from(enabledNames);
};

/**
 * Determines if a key fact can be edited.
 *
 * A key fact is uneditable iff its source corresponds to a currently-enabled
 * integration (v1 IAccountIntegrations or v2 IExternalIntegration).
 * Anything else —- user-created (no source), CsvImport, Zapier, disabled
 * integrations, or other unrecognized sources —- is editable.
 *
 * @param keyFact - The key fact to check
 * @param integrations - The account's v1 integration settings
 * @param externalIntegrations - The account's v2 integration settings (optional)
 * @returns true if the key fact can be edited/deleted, false otherwise
 */
export const canEditKeyFact = (
	keyFact: Api.IKeyFact,
	integrations: Api.IAccountIntegrations,
	externalIntegrations?: Partial<Api.IExternalIntegration>[]
): boolean => {
	// IKeyFactSource.source is declared as Api.IntegrationSources, but this
	// field can contain values outside that enum (e.g., 'Zapier'), forcing us
	// to cast as a string.
	const source = keyFact.source?.source as string | undefined;
	return !isSourceEnabledIntegration(source, integrations, externalIntegrations);
};
